import { useEffect, useState } from "react";
import { Share, PlusSquare, Check, Smartphone, X, Play, PlugZap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ManualeEv } from "./ManualeEv";

// "Aggiungi a Home": prompt nativo (Android/desktop) o guida in 3 passi (iPhone)
let deferred = null;
if (typeof window !== "undefined") window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; });

const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const inApp = isIOS && (!/Safari\//.test(ua) || /CriOS|FxiOS|GSA|FBAN|FBAV|Instagram|WhatsApp/.test(ua));
const K = "torri_home_invito";
const leggi = () => { try { return JSON.parse(localStorage.getItem(K)) || {}; } catch { return {}; } };
const scrivi = (v) => { try { localStorage.setItem(K, JSON.stringify(v)); } catch { /* privato */ } };

export const disponibile = () => !isStandalone() && !window.__torriAppInstallata && !leggi().fatto && /^\/cliente\//.test(window.location.pathname);
// l'app è già stata aperta dall'icona su questo link: niente più inviti
export const segnaInstallata = (v) => { if (v) { window.__torriAppInstallata = true; } };
// inviti nei "momenti giusti" (report scaricato, record, condivisione)
export const invitoMomento = (testo) => window.dispatchEvent(new CustomEvent("torri-invito", { detail: testo }));
const ATTESE = [3, 7, 15, 30];
const vestaAperta = () => !!document.querySelector('[data-testid="vesta-sheet"]');

async function installa(apriGuida) {
  if (deferred) {
    deferred.prompt();
    const r = await deferred.userChoice.catch(() => null);
    deferred = null;
    if (r?.outcome === "accepted") scrivi({ ...leggi(), fatto: true });
    return;
  }
  apriGuida();
}

// banner in fondo alla home: dal 2° accesso, dopo 20 s, max una volta al giorno; "No grazie" lo rimanda (3, 7, 15 giorni, poi una volta al mese) finché l'app non viene installata
export function InstallaBanner() {
  const [show, setShow] = useState(false);
  const [testo, setTesto] = useState("");
  const [guida, setGuida] = useState(false);
  useEffect(() => {
    if (!disponibile()) return undefined;
    const oggi = new Date().toDateString();
    const mostra = (t) => {
      const s = leggi();
      if (!disponibile() || s.mostrato === oggi) return;
      if (vestaAperta()) { setTimeout(() => mostra(t), 20000); return; }
      scrivi({ ...s, mostrato: oggi });
      setTesto(t || ""); setShow(true);
    };
    const s = leggi();
    const v = { ...s, accessi: (s.accessi || 0) + (s.ultimo === oggi ? 0 : 1), ultimo: oggi };
    scrivi(v);
    const rif = v.rifiuti || 0, attesa = rif ? ATTESE[Math.min(rif - 1, ATTESE.length - 1)] * 864e5 : 0;
    const t = v.accessi >= 2 && Date.now() - (v.rifiutato_at || 0) >= attesa ? setTimeout(() => mostra(), 20000) : null;
    const onInvito = (e) => mostra(e.detail);
    window.addEventListener("torri-invito", onInvito);
    return () => { clearTimeout(t); window.removeEventListener("torri-invito", onInvito); };
  }, []);
  const no = () => { const s = leggi(); scrivi({ ...s, rifiuti: (s.rifiuti || 0) + 1, rifiutato_at: Date.now() }); setShow(false); };
  return (
    <>
      {show && (
        <div className="fixed inset-x-3 bottom-24 z-30 mx-auto flex max-w-md items-center gap-3 rounded-3xl bg-slate-900 p-3 pr-4 text-white shadow-2xl ring-1 ring-white/10 animate-in fade-in slide-in-from-bottom-4" data-testid="cliente-home-banner">
          <img src="/torri-cliente-icon-192.png?v=1" alt="" className="h-12 w-12 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1">
            <b className="block text-sm font-extrabold leading-tight" data-testid="cliente-home-banner-testo">{testo || "Tieni il tuo impianto a portata di mano"}</b>
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={() => installa(() => setGuida(true))} className="rounded-full bg-yellow-400 px-3 py-1.5 text-xs font-extrabold text-black transition-transform active:scale-95" data-testid="cliente-home-banner-come">Come si fa</button>
              <button type="button" onClick={no} className="rounded-full px-3 py-1.5 text-xs font-bold text-white/70 hover:text-white" data-testid="cliente-home-banner-no">No grazie</button>
            </div>
          </div>
          <button type="button" onClick={no} aria-label="Chiudi" className="self-start p-1 text-white/50 hover:text-white" data-testid="cliente-home-banner-chiudi"><X className="h-4 w-4" /></button>
        </div>
      )}
      <InstallaGuida open={guida} onClose={() => { setGuida(false); setShow(false); }} />
    </>
  );
}

// etichetta fissa e discreta accanto al nome dell'impianto
export function InstallaPill() {
  const [guida, setGuida] = useState(false);
  if (!disponibile()) return null;
  return (
    <>
      <button type="button" onClick={() => installa(() => setGuida(true))} data-testid="cliente-installa-pill"
        className="mt-1 inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-0.5 text-[11px] font-bold text-sky-700 ring-1 ring-sky-100 transition-colors hover:bg-sky-100">
        <Smartphone className="h-3 w-3" />Installa app
      </button>
      <InstallaGuida open={guida} onClose={() => setGuida(false)} />
    </>
  );
}

// voce fissa nella scheda Impianto
export function InstallaVoce() {
  const [guida, setGuida] = useState(false);
  if (!disponibile()) return null;
  return (
    <>
      <button type="button" onClick={() => installa(() => setGuida(true))} className="flex w-full items-center gap-3 rounded-3xl bg-white p-4 text-left shadow-sm ring-1 ring-slate-100 transition-colors hover:bg-slate-50" data-testid="cliente-home-voce">
        <img src="/torri-cliente-icon-192.png?v=1" alt="" className="h-11 w-11 rounded-xl" />
        <span className="min-w-0 flex-1"><b className="block text-sm font-extrabold text-slate-800">Aggiungi alla schermata Home</b>
          <span className="block text-xs text-slate-500">Apri il tuo impianto con un tocco, come un'app</span></span>
        <Smartphone className="h-5 w-5 text-sky-600" />
      </button>
      <InstallaGuida open={guida} onClose={() => setGuida(false)} />
    </>
  );
}

const Passo = ({ n, I, children }) => (
  <li className="flex items-center gap-3 rounded-2xl bg-slate-50 px-3 py-2.5">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-xs font-extrabold text-black">{n}</span>
    <span className="flex-1 text-sm text-slate-700">{children}</span>
    {I && <I className="h-5 w-5 shrink-0 text-sky-600" />}
  </li>
);

// sezione Tutorial della scheda Impianto: per ora "Trasformala in app" (guida + video)
const TUTORIAL = [
  { os: "ios", t: "Trasformala in app su iPhone", d: "Video: salva l'icona TORRI sulla Home in 3 tocchi", video: true },
  { os: "android", t: "Trasformala in app su Android", d: "Installa l'icona TORRI dal browser Chrome" },
  { os: "desktop", t: "Trasformala in app sul computer", d: "Video: apri il tuo impianto in una finestra, senza barra del browser", video: true },
];

// sezione Tutorial della scheda Impianto: guide "Trasformala in app" per iPhone (video), Android e computer (testo)
export function Tutorial() {
  const [os, setOs] = useState(null);
  const [manuale, setManuale] = useState(false);
  return (
    <section className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-100" data-testid="cliente-tutorial">
      <h2 className="mb-3 text-base font-extrabold text-slate-800">Tutorial</h2>
      <div className="space-y-2">
        <button type="button" onClick={() => setManuale(true)} className="flex w-full items-center gap-3 rounded-2xl bg-sky-50 p-3 text-left transition-colors hover:bg-sky-100" data-testid="cliente-tutorial-manuale-ev">
          <span className="flex h-16 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-600 text-white ring-1 ring-sky-200"><PlugZap className="h-6 w-6" /></span>
          <span className="min-w-0 flex-1"><b className="block text-sm font-extrabold text-slate-800">Ricarica auto: guida completa</b>
            <span className="block text-xs text-slate-500">Come funziona l'EV charge e tutte le funzioni smart (sole, PUN, programma) con le regolazioni</span></span>
        </button>
        {TUTORIAL.map((x) => (
          <button key={x.os} type="button" onClick={() => setOs(x.os)} className="flex w-full items-center gap-3 rounded-2xl bg-slate-50 p-3 text-left transition-colors hover:bg-slate-100" data-testid={`cliente-tutorial-${x.os}`}>
            <span className="relative flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-900 ring-1 ring-slate-200">
              {x.os === "ios" ? <img src="/guida-iphone-home.jpg?v=1" alt="" className="h-full w-full object-cover object-top" />
                : x.os === "desktop" ? <img src="/tutorial-desktop-poster.jpg?v=1" alt="" className="h-full w-full object-cover" />
                : <Smartphone className="h-6 w-6 text-emerald-400" />}
              {x.video && <span className="absolute flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white"><Play className="ml-0.5 h-3.5 w-3.5 fill-current" /></span>}
            </span>
            <span className="min-w-0 flex-1"><b className="block text-sm font-extrabold text-slate-800">{x.t}</b>
              <span className="block text-xs text-slate-500">{x.d}</span></span>
          </button>
        ))}
      </div>
      <InstallaGuida open={!!os} onClose={() => setOs(null)} os={os} />
      <ManualeEv open={manuale} onClose={() => setManuale(false)} />
    </section>
  );
}

export function InstallaGuida({ open, onClose, os }) {
  const sist = os || (isIOS ? "ios" : /Android/.test(ua) ? "android" : "desktop");
  const ios = sist === "ios", desktop = sist === "desktop";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="cliente-font max-w-sm rounded-3xl bg-white text-slate-800" data-testid="cliente-home-guida">
        <DialogHeader>
          <DialogTitle className="text-lg font-extrabold">Aggiungi «Mio TORRI» alla Home</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Così apri il tuo impianto con un tocco, senza cercare il link.</DialogDescription>
        </DialogHeader>
        {inApp && ios && <p className="rounded-2xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="cliente-home-guida-safari">Prima apri questa pagina in <b>Safari</b>: tocca <b>«…»</b> (o l'icona della bussola) e scegli <b>«Apri in Safari»</b>.</p>}
        {ios && <figure className="text-center"><video src="/guida-iphone-home.mp4?v=2" poster="/guida-iphone-home.jpg?v=1" className="mx-auto max-h-[46vh] rounded-2xl shadow-md ring-1 ring-slate-200" autoPlay muted loop playsInline data-testid="cliente-home-guida-video" />
</figure>}
        {desktop && <figure className="text-center"><video src="/tutorial_sfocato.mp4?v=1" poster="/tutorial-desktop-poster.jpg?v=1" controls playsInline className="mx-auto max-h-[46vh] w-full rounded-2xl shadow-md ring-1 ring-slate-200" data-testid="cliente-home-guida-video-desktop" /></figure>}
        <ol className="space-y-2">
          {ios ? <>
            <Passo n={1} I={Share}>Tocca il pulsante <b>Condividi</b> in basso (il quadrato con la freccia in su)</Passo>
            <Passo n={2} I={PlusSquare}>Scorri e scegli <b>«Aggiungi alla schermata Home»</b></Passo>
            <Passo n={3} I={Check}>Tocca <b>«Aggiungi»</b> in alto a destra</Passo>
          </> : desktop ? <>
            <Passo n={1}><b>Chrome o Edge:</b> nella barra dell'indirizzo tocca l'icona <b>Installa</b> (lo schermo con la freccia), oppure menu <b>⋮</b> → <b>«Trasmetti, salva e condividi»</b> → <b>«Installa pagina come app»</b></Passo>
            <Passo n={2} I={Check}>Conferma con <b>«Installa»</b>: «Mio TORRI» si apre in una finestra sua e compare sul desktop</Passo>
            <Passo n={3}><b>Safari su Mac:</b> menu <b>File</b> → <b>«Aggiungi al Dock»</b> → <b>«Aggiungi»</b></Passo>
            <Passo n={4}>Se usi <b>«Crea scorciatoia»</b>, spunta <b>«Apri come finestra»</b>: altrimenti si apre come una normale scheda</Passo>
          </> : <>
            <Passo n={1}>Apri questa pagina in <b>Chrome</b> (se arrivi da WhatsApp o Gmail: menu <b>⋮</b> → <b>«Apri in Chrome»</b>)</Passo>
            <Passo n={2}>Tocca il menu <b>⋮</b> in alto a destra</Passo>
            <Passo n={3} I={PlusSquare}>Scegli <b>«Installa app»</b> (o <b>«Aggiungi a schermata Home»</b> → <b>«Installa»</b>)</Passo>
            <Passo n={4} I={Check}>Conferma: l'icona «Mio TORRI» compare sulla Home e si apre senza barra del browser</Passo>
          </>}
        </ol>
      </DialogContent>
    </Dialog>
  );
}
